/**
 * LeaseSignaturePage — Page publique de signature électronique (lien magique).
 *
 * URL : /sign/{token}
 *
 * NE NÉCESSITE PAS d'auth — le token UUID secret EST l'authentification.
 *
 * Workflow :
 * 1. GET /api/public/lease-signature/{token} → infos bail + signataire
 * 2. Affiche le PDF preview (iframe embed)
 * 3. Canvas pour dessiner la signature (mouse/touch/stylus)
 * 4. Checkbox consentement
 * 5. POST /api/public/lease-signature/{token}/sign avec image base64
 * 6. Page de confirmation après signature
 *
 * Conforme : Loi sur le commerce électronique du Québec (RLRQ c C-1.1).
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FileSignature, Loader2, CheckCircle2, AlertCircle, RotateCcw, Shield,
  ShieldAlert, ExternalLink,
} from "lucide-react";
import { toast } from "@/lib/toast";
import LongOperationOverlay from "@/components/shared/LongOperationOverlay";
import { API } from "@/api";


export default function LeaseSignaturePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [allSigned, setAllSigned] = useState(false);

  // Canvas refs
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Batch #14.2 — timeout explicite 25s pour éviter spinner infini si PROD lent
        const r = await axios.get(`${API}/public/lease-signature/${token}`, { timeout: 25000 });
        setInfo(r.data);
        if (r.data?.signer?.has_signed) {
          setSigned(true);
        }
      } catch (e) {
        // Distinction explicit pour aider le debug PROD
        if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
          setError("Le serveur met trop de temps à répondre. Veuillez réessayer dans quelques secondes ou contactez votre cabinet comptable.");
        } else if (e.response?.status === 404 || e.response?.status === 410) {
          setError(e.response?.data?.detail || "Lien invalide ou expiré.");
        } else if (!e.response) {
          // Erreur réseau (CORS, DNS, offline)
          setError(`Connexion impossible au serveur. Vérifiez votre connexion internet ou contactez votre cabinet comptable. (${e.message || "erreur réseau"})`);
        } else {
          setError(e.response?.data?.detail || `Erreur ${e.response?.status} — ${e.message || "inconnue"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  // Setup canvas
  useEffect(() => {
    if (signed || loading || error) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#0f172a";
    ctxRef.current = ctx;
  }, [signed, loading, error]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const sx = c.width / rect.width;
    const sy = c.height / rect.height;
    const x = ((e.touches?.[0]?.clientX ?? e.clientX) - rect.left) * sx;
    const y = ((e.touches?.[0]?.clientY ?? e.clientY) - rect.top) * sy;
    return { x, y };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    drawingRef.current = true;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
    hasDrawnRef.current = true;
  };
  const stopDraw = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    ctxRef.current.clearRect(0, 0, c.width, c.height);
    hasDrawnRef.current = false;
  };

  const submit = async () => {
    if (!hasDrawnRef.current) {
      toast.error("Veuillez dessiner votre signature.");
      return;
    }
    if (!consent) {
      toast.error("Veuillez accepter la signature électronique.");
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      // Batch #14.49 — Timeout généreux (90s) car le dernier signataire déclenche
      // merge PDF + audit + upload Drive + emails à tous (peut dépasser 60s sur LTE).
      const r = await axios.post(`${API}/public/lease-signature/${token}/sign`, {
        signature_image_b64: dataUrl,
        consent: true,
      }, { timeout: 90000 });
      setSigned(true);
      setAllSigned(!!r.data?.all_signed);
      toast.success("Signature enregistrée !");
    } catch (e) {
      if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
        toast.error("Le traitement prend plus de temps que prévu. Vérifiez votre courriel — votre signature a peut-être quand même été enregistrée.");
      } else {
        toast.error(e.response?.data?.detail || "Erreur lors de la signature.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // States
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 p-6" data-testid="lease-sign-loading">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
        <div className="text-center max-w-sm">
          <div className="text-base font-semibold text-slate-800 mb-1">Chargement du bail à signer...</div>
          <div className="text-xs text-slate-500">
            Connexion sécurisée au serveur de DG Comptabilité. Cela peut prendre quelques secondes.
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-rose-200 rounded-2xl p-8 max-w-md text-center" data-testid="lease-sign-error">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Lien indisponible</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="lease-sign-error-retry"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Réessayer
          </button>
        </div>
      </div>
    );
  }
  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-emerald-200 rounded-2xl p-8 max-w-md text-center shadow-sm" data-testid="lease-sign-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Signature enregistrée</h1>
          <p className="text-sm text-slate-600 mb-3">
            Merci <b>{info?.signer?.name}</b>. Votre signature a été archivée avec horodatage,
            IP et empreinte du document.
          </p>
          {allSigned ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
              ✓ Toutes les parties ont signé. Le bail est désormais <b>finalisé</b>.
              Une copie signée a été envoyée par courriel à toutes les parties.
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              En attente de signature des autres parties. Vous recevrez une notification
              lorsque le bail sera finalisé.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lease-sign-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <FileSignature className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-slate-800 truncate">Signature de bail — {info?.property_address}</h1>
            <p className="text-[11px] text-slate-500">
              {info?.signer?.role === "tenant" ? "Locataire" : "Propriétaire"} : <b>{info?.signer?.name}</b>
            </p>
          </div>
          <div className="text-[10px] text-slate-400 hidden sm:block">
            <Shield className="w-3 h-3 inline mr-1" /> Conforme Québec
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Preview PDF — Batch #14.1 : skeleton + bandeau pour éviter perception "white screen" */}
        <div className="lg:col-span-2">
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <span>Document à signer</span>
            <span className="text-[10px] text-slate-400 italic">— le chargement peut prendre quelques secondes</span>
          </h2>
          <PdfPreviewWithLoading
            src={`${API}/public/lease-signature/${token}/pdf`}
            testId="lease-sign-pdf-preview"
          />
        </div>

        {/* Signature panel */}
        <div className="space-y-4">
          {/* Recap */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-semibold text-slate-800">Conditions clés</h3>
            <div className="text-xs space-y-1">
              <p className="text-slate-600">Logement : <b>{info?.property_address}{info?.unit_label ? ` · apt ${info.unit_label}` : ""}</b></p>
              <p className="text-slate-600">Loyer mensuel : <b>{info?.rent_amount ? `${Number(info.rent_amount).toFixed(2)} $` : "—"}</b></p>
              <p className="text-slate-600">Du <b>{info?.start_date || "—"}</b> au <b>{info?.end_date || "—"}</b></p>
            </div>
          </div>

          {/* Other signers status */}
          {info?.all_signers_status?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Toutes les parties</h3>
              <ul className="space-y-1.5 text-xs">
                {info.all_signers_status.map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {s.signed ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                    )}
                    <span className={s.signed ? "text-slate-700" : "text-slate-500"}>
                      {s.name} <span className="text-slate-400">({s.role === "tenant" ? "Locataire" : "Propriétaire"})</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Canvas */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Votre signature</h3>
              <button
                onClick={clearCanvas}
                className="text-[11px] text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                data-testid="lease-sign-clear"
              >
                <RotateCcw className="w-3 h-3" /> Effacer
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={500}
              height={180}
              className="w-full h-44 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg cursor-crosshair touch-none"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
              data-testid="lease-sign-canvas"
            />
            <p className="text-[10px] text-slate-400 mt-1">Signez avec votre souris ou votre doigt sur écran tactile.</p>
          </div>

          {/* Consent */}
          <label className="flex items-start gap-2 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg p-3">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 flex-shrink-0"
              data-testid="lease-sign-consent"
            />
            <span className="text-[11px] text-amber-900 leading-snug">
              J'accepte de signer ce document par voie électronique. Ma signature a la même valeur
              légale qu'une signature manuscrite (Loi concernant le cadre juridique des technologies
              de l'information, RLRQ c C-1.1).
            </span>
          </label>

          {/* Batch #14.49 — Avertissement temps de traitement avant clic */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-testid="lease-sign-time-warning">
            <Loader2 className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-blue-900 leading-relaxed">
              <strong>Patience requise :</strong> Le traitement de la signature peut prendre
              {' '}<strong>jusqu'à 60 secondes</strong> sur réseau mobile (LTE/3G).
              Nous archivons votre signature et préparons les copies. <strong>Ne fermez pas la fenêtre</strong> pendant le traitement.
            </p>
          </div>

          {/* Submit */}
          <button
            onClick={submit}
            disabled={submitting || !consent}
            className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="lease-sign-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
            Signer le bail
          </button>
        </div>
      </div>

      {/* Batch #14.49 — Overlay bloquant pendant la soumission */}
      <LongOperationOverlay
        visible={submitting}
        title="Signature en cours…"
        message="Nous archivons votre signature avec horodatage, IP et empreinte du document. Si vous êtes le dernier signataire, nous générons aussi le PDF signé final et l'envoyons aux parties."
        hint="Le dernier signataire déclenche l'envoi des courriels (jusqu'à 90 secondes sur réseau mobile)."
        testId="lease-sign-submitting-overlay"
      />
    </div>
  );
}

// Batch #14.1 — Wrapper avec skeleton de chargement pour le PDF iframe
// Batch #14.6 — Ajout du fallback "ouvrir dans un nouvel onglet" si l'iframe est bloquée
// (Check Point, antivirus d'entreprise, X-Frame restrictifs, etc.). Après ~10s sans
// événement onLoad, on bascule sur le mode fallback avec un gros bouton bien visible.
// Batch #14.39 — Sur iOS / mobile, l'iframe PDF ne s'affiche JAMAIS (limitation Safari iOS).
// On force directement le fallback "Ouvrir le PDF" sur ces appareils.
// Batch #14.48 — Ajout du message d'avertissement sur le temps de téléchargement mobile
// (45-60s sur LTE/3G) pour éviter que l'utilisateur pense que c'est planté.
function PdfPreviewWithLoading({ src, testId }) {
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [secondsSinceClick, setSecondsSinceClick] = useState(0);

  // Détection iOS/mobile : Safari iOS et la plupart des navigateurs mobiles
  // refusent d'afficher des PDF dans des iframes. On affiche directement le fallback.
  const isMobileOrIOS = (() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iOS 13+ sur iPad envoie un user-agent desktop → on regarde aussi le platform/touch
    const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    const isAndroid = /Android/.test(ua);
    const isMobile = /Mobile|Tablet/.test(ua);
    return isIOS || isIPadOS || isAndroid || isMobile;
  })();

  // Watchdog : si l'iframe ne charge pas dans 10s, on assume qu'elle est bloquée
  useEffect(() => {
    if (loaded || isMobileOrIOS) return;
    const timer = setTimeout(() => {
      if (!loaded) setBlocked(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, [loaded, isMobileOrIOS]);

  // Batch #14.48 — Timer mobile post-clic pour afficher un compte-à-rebours rassurant
  useEffect(() => {
    if (!clicked) return;
    setSecondsSinceClick(0);
    const interval = setInterval(() => {
      setSecondsSinceClick((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [clicked]);

  const showFallback = isMobileOrIOS || (blocked && !loaded);

  return (
    <div className="relative w-full h-[70vh] border border-slate-200 rounded-xl bg-white overflow-hidden">
      {/* Fallback prioritaire : iOS/Android (toujours) OU iframe bloquée */}
      {showFallback && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-amber-50 to-rose-50 z-20 p-6 overflow-y-auto" data-testid="lease-sign-pdf-fallback">
          <div className="w-16 h-16 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-8 h-8 text-amber-600" />
          </div>
          <div className="text-center max-w-md">
            <h3 className="text-base font-bold text-slate-800 mb-1">
              {isMobileOrIOS
                ? "Ouverture du document"
                : "Votre système de sécurité bloque l'affichage du document"}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              {isMobileOrIOS ? (
                <>
                  Sur téléphone et tablette, le bail s'ouvre dans un nouvel onglet pour une
                  meilleure lisibilité. Cliquez ci-dessous pour <strong>consulter le document</strong>,
                  puis revenez ici pour signer.
                </>
              ) : (
                <>
                  Un pare-feu, antivirus ou navigateur d'entreprise (Check Point, McAfee, etc.)
                  peut empêcher l'affichage intégré des PDF. Cliquez ci-dessous pour ouvrir le document
                  <strong> directement et de façon sécurisée </strong> dans un nouvel onglet.
                </>
              )}
            </p>
          </div>

          {/* Batch #14.48 — Avertissement temps de chargement sur mobile (avant clic) */}
          {isMobileOrIOS && !clicked && (
            <div className="w-full max-w-md bg-white/80 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="lease-sign-pdf-mobile-warning">
              <Loader2 className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-[11px] text-amber-900 leading-relaxed text-left">
                <strong>Patience requise :</strong> Le téléchargement peut prendre
                {' '}<strong>30 à 60 secondes</strong> sur réseau mobile (LTE/3G).
                L'écran peut sembler noir un moment — <strong>c'est normal</strong>,
                ne fermez pas la fenêtre.
              </div>
            </div>
          )}

          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isMobileOrIOS && setClicked(true)}
            className="px-6 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-md inline-flex items-center gap-2 transition"
            data-testid="lease-sign-pdf-fallback-open"
          >
            <ExternalLink className="w-5 h-5" /> {isMobileOrIOS ? "Ouvrir le bail" : "Ouvrir le bail dans un nouvel onglet"}
          </a>

          {/* Batch #14.48 — Message rassurant après clic mobile (état actif pendant ~60s) */}
          {isMobileOrIOS && clicked && (
            <div className="w-full max-w-md bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 space-y-2" data-testid="lease-sign-pdf-mobile-loading-info">
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-700 flex-shrink-0" />
                <strong className="text-sm text-emerald-900">Téléchargement en cours…</strong>
              </div>
              <p className="text-[11px] text-emerald-900 leading-relaxed">
                Le bail se télécharge dans le nouvel onglet ouvert. Sur réseau mobile, cela peut prendre
                {' '}<strong>jusqu'à 60 secondes</strong>. <strong>Ne fermez pas la fenêtre noire</strong> — le document va apparaître.
              </p>
              <div className="text-[11px] text-emerald-700 font-mono pt-1 border-t border-emerald-200">
                ⏱ Temps écoulé : <strong>{secondsSinceClick}s</strong>
                {secondsSinceClick > 60 && (
                  <span className="ml-2 text-rose-700">
                    Pas de réponse ? <button onClick={() => setClicked(false)} className="underline font-semibold">Réessayer</button>
                  </span>
                )}
              </div>
            </div>
          )}

          {!isMobileOrIOS && (
            <button
              onClick={() => { setBlocked(false); setLoaded(false); /* trigger reload */ }}
              className="text-[11px] text-slate-500 hover:text-slate-700 underline"
              data-testid="lease-sign-pdf-fallback-retry-iframe"
            >
              Réessayer l'affichage intégré
            </button>
          )}
        </div>
      )}

      {/* Skeleton de chargement (pendant les 10 premières secondes, desktop seulement) */}
      {!loaded && !showFallback && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-50/80 z-10" data-testid="lease-sign-pdf-loading">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
          <div className="text-sm font-semibold text-slate-700">Chargement du document...</div>
          <div className="text-[11px] text-slate-500 max-w-xs text-center px-4">
            Le formulaire officiel TAL peut prendre quelques secondes à s'afficher. Merci de patienter.
          </div>
        </div>
      )}
      {/* L'iframe est rendue uniquement sur desktop. Sur mobile/iOS on saute. */}
      {!isMobileOrIOS && (
        <iframe
          title="Bail PDF"
          src={src}
          onLoad={() => { setLoaded(true); setBlocked(false); }}
          onError={() => setBlocked(true)}
          className="w-full h-full bg-white"
          data-testid={testId}
        />
      )}
    </div>
  );
}
